/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  CSSProperties,
  ElementRef,
  memo,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {Rnd} from 'react-rnd';
import {makeStyles} from '@material-ui/core';
import isEqual from 'lodash.isequal';
import {
  ResizableDraggableContent, ResizableDraggableContentProps
} from '@ion/dls/src/components/resizable/ResizableDraggableContent';
import {
  ResizeGrabber
} from '@ion/dls/src/components/resizable/ResizeGrabber';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsLayout, dlsZIndex} from '@ion/dls/src/theme/theme';
import {useThemeContext} from '@ion/dls/src/theme/useThemeContext';

// the function returns the height of the bottom table from theme...
export const getTableHeight = () => dlsLayout('bottomTable', 'height');

interface StyleProps {
  initialHeight?: number;
}

// defines the props definitions for the ResizableBottomSheet...
interface BottomSheetProps extends ResizableDraggableContentProps {
  isBottomSheetOpen?: boolean;
  onHandleResizeStop?: Function;
  autoClose?: boolean;
  breadCrumbComponent?: ReactElement;
  // opts a caller into the resizable/draggable behavior on mobile too -
  // by default this component bypasses resizing entirely on mobile...
  enableOnMobile?: boolean;
};

export type ResizableBottomSheetProps = BottomSheetProps & StyleProps;

// defines the styles definitions for the ResizableBottomSheet...
const useStyles = makeStyles(theme => ({
  resizableRoot : {
    height : ({initialHeight}: StyleProps) => `${initialHeight ?? getTableHeight()} !important`
  },
  resizeHandleTop : toggleThemeStyle({
    display        : 'flex !important',
    cursor         : 'initial !important',
    justifyContent : 'center',
    padding        : '4px 0'
  }, {
    display         : 'flex !important',
    cursor          : 'initial !important',
    justifyContent  : 'center',
    alignItems      : 'flex-end',
    height          : 'auto !important',
    padding         : '0px',
    top             : '-32px !important',
    backgroundColor : 'transparent !important'
  })
}));

/**
 * Defines the ResizableBottomSheet component. This is a resizable bottom sheet which is a
 * HOC that only wraps the children's content in a resizable element when on desktop.
 *
 * ```typescript
 *
 *  import {ResizableBottomSheet} from '@ion/dls/src/components/resizable/ResizableBottomSheet';
 *
 *   <ResizableBottomSheet isBottomSheetOpen>
 *     <TrackingTable {..props} />
 *   </ResizableBottomSheet>
 *
 * ```
 */
export const ResizableBottomSheet = ({
  children,
  onHandleResizeStop,
  initialHeight,
  maxHeight = '97%',
  isBottomSheetOpen = true,
  autoClose = false,
  breadCrumbComponent = null,
  enableOnMobile = false
}: ResizableBottomSheetProps) => {
  const transitionTimeMs  = 400;
  const {isPhone}         = useMedia();
  const {isCurrent, mode} = useThemeContext();
  const resizeRef: any    = useRef<Rnd>(null);
  const sheetHeightRef    = useRef(0);
  const defaultSize       = useMemo(() => ({width: 'auto', height: 0, x: 0, y: 0}), []);
  const classes           = useStyles({
    initialHeight : initialHeight ? `${initialHeight}px` : null
  });
  const [resizeClassState, setResizeClassState] = useState(classes.resizableRoot);

  const resetSize = () => {
    setTimeout(() => resizeRef?.current?.updateSize({width: 'auto', height: 0}), 150);
    setResizeClassState(null);
  };

  useEffect(() => {
    isBottomSheetOpen ? setResizeClassState(classes.resizableRoot) : resetSize();
  }, [isBottomSheetOpen]);

  const handleResizeStop = useCallback(function(
    e: MouseEvent | TouchEvent,
    dir: ResizableDraggableContentProps['ResizeDirection'],
    refToElement: ElementRef<'div'>
  ) {
    refToElement.style.transition = `height ${transitionTimeMs}ms cubic-bezier(0.4, 0.0, 0.2, 1)`;

    const {offsetHeight: bottomSheetContentHeight} = refToElement;
    sheetHeightRef.current = bottomSheetContentHeight;
    onHandleResizeStop?.(bottomSheetContentHeight);

    // If the user sizes the history content too small we snap the element shut
    // to avoid a degraded user experience.
    // 250px (or greater) is the height in which the history content is viewable with
    // a reasonable user experience.
    if (autoClose && bottomSheetContentHeight < 250) {
      refToElement.style.height = '0px';
      resetSize();
    }
  }, [isBottomSheetOpen]);

  // we need to remove it or else there is a delay when the user drags the element.
  const handleResizeStart: any = useCallback(function(
    e: MouseEvent | TouchEvent,
    dir: ResizableDraggableContentProps['ResizeDirection'],
    refToElement: ElementRef<'div'>
  ) {
    refToElement.style.transition = null;
    !!resizeClassState && setResizeClassState(null);
  }, [resizeClassState, isBottomSheetOpen]);

  const enableResizing = useMemo(() => {
    return {
      top         : isBottomSheetOpen,
      right       : false,
      bottom      : false,
      left        : false,
      topRight    : false,
      bottomRight : false,
      bottomLeft  : false,
      topLeft     : false
    };
  }, [isBottomSheetOpen]);

  const style: CSSProperties = useMemo(() => {
    return {
      // the elevated desktop z-index only exists to stay correctly layered
      // against other desktop-only elements it may visually overlap during
      // a resize drag - mobile never reached this code path before
      // `enableOnMobile` existed, so nothing on mobile (e.g. the page's
      // floating add button, a bottom-sheet Drawer) was ever designed to
      // sit above it; normal document stacking order is sufficient there.
      zIndex          : isPhone ? undefined : dlsZIndex('resizableBottomSheetDraggableContent'),
      backgroundColor : dlsColor('white'),
      position        : 'relative',
      marginLeft      : '1px',
      display         : 'flex',
      // the current theme stacks the sheet's content; the legacy sheet keeps
      // its original row, which stretches the grid to the sheet's height ...
      ...(isCurrent && {flexDirection: 'column' as const}),
      transition      : `height ${transitionTimeMs}ms cubic-bezier(0.4, 0.0, 0.2, 1)`
    };
  }, [isCurrent, isPhone, mode]);

  return (
    (!isPhone || enableOnMobile) ? (
      <ResizableDraggableContent maxHeight={maxHeight}
        dragAxis='both'
        defaultSize={defaultSize}
        resizeRef={resizeRef}
        onResizeStop={handleResizeStop}
        onResizeStart={handleResizeStart}
        enableResizing={enableResizing}
        style={style}
        className={resizeClassState}
        resizeHandleClasses={{
          top : classes.resizeHandleTop
        }}
        resizeHandleComponent={{
          top : <ResizeGrabber breadCrumbComponent={breadCrumbComponent} />
        }}>
        {children}
      </ResizableDraggableContent>
    ) : (
      <>
        {children}
      </>
    )
  );
};

export default memo(ResizableBottomSheet, isEqual);
